import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button } from "antd";
import {
  AppstoreOutlined,
  BarChartOutlined,
  CommentOutlined,
  FormOutlined,
  IdcardOutlined,
  NodeIndexOutlined,
  ShopOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { useCopilotShell } from "contexts/CopilotShellContext";
import {
  getLockedNavKeys,
  lockedProductKeysForDiscover,
  ownedProductKeysForDiscover,
} from "util/navProductAccess";
import { PRODUCT_LOCK } from "util/productLockConfig";
import { SONIC_PROSPECT_PRICING } from "util/prospectBilling";
import { trackAddonLockCtaClicked } from "util/productAnalytics";
import AdvisorIcon from "components/Copilot/AdvisorIcon";
import styles from "./style.module.scss";

const PRODUCT_ICONS = {
  voice_of_customer: AppstoreOutlined,
  customer_journey: NodeIndexOutlined,
  market_intelligence: BarChartOutlined,
  customer_365: IdcardOutlined,
  sonic_engage: ThunderboltOutlined,
  sonic_prospect: CommentOutlined,
  survey: FormOutlined,
};

const productTitle = (t, productKey, spec) => {
  if (productKey === "survey") return t("discover_products.title.survey");
  return t(`${spec.i18nKey}.eyebrow`);
};

const ProductIcon = ({ productKey, chip }) => {
  if (productKey === "advisor") {
    return <AdvisorIcon size={chip ? "xs" : "sm"} />;
  }
  const Icon = PRODUCT_ICONS[productKey] || AppstoreOutlined;
  return <Icon />;
};

const DiscoverProducts = ({ theme: themeProp }) => {
  const { t } = useTranslation();
  const subscriptionStatus = useSelector((state) => state.userReducer);
  const dashboardTheme = useSelector((state) => state.DashboardReducer.theme);
  const theme = themeProp || dashboardTheme || "light";
  const { hasCxGptAccess, cxGptAccessLoading } = useCopilotShell();

  const lockedKeys = useMemo(() => {
    const extras = {
      hasAdvisorAccess: hasCxGptAccess,
      advisorAccessReady: !cxGptAccessLoading,
    };
    return lockedProductKeysForDiscover(
      getLockedNavKeys(subscriptionStatus, extras)
    );
  }, [subscriptionStatus, hasCxGptAccess, cxGptAccessLoading]);

  const ownedKeys = useMemo(
    () =>
      ownedProductKeysForDiscover(subscriptionStatus, {
        hasAdvisorAccess: hasCxGptAccess,
        advisorAccessReady: !cxGptAccessLoading,
      }),
    [subscriptionStatus, hasCxGptAccess, cxGptAccessLoading]
  );

  return (
    <div
      className={`${styles.page} ${
        theme === "dark" ? styles.pageDark : styles.pageLight
      }`}
    >
      <h1>{t("nav.discover_products")}</h1>
      <p className={styles.lede}>{t("discover_products.lede")}</p>

      {ownedKeys.length > 0 && (
        <div className={styles.planRow}>
          <span className={styles.planLabel}>
            {t("discover_products.in_your_plan")}
          </span>
          {ownedKeys.map((productKey) => {
            const spec = PRODUCT_LOCK[productKey];
            if (!spec) return null;
            return (
              <span key={productKey} className={styles.planChip}>
                <ProductIcon productKey={productKey} chip />
                {productTitle(t, productKey, spec)}
              </span>
            );
          })}
        </div>
      )}

      {lockedKeys.length > 0 && (
        <div className={styles.sectionLabel}>
          {t("discover_products.available_to_add")}
        </div>
      )}

      <div className={styles.grid}>
        {lockedKeys.map((productKey) => {
          const spec = PRODUCT_LOCK[productKey];
          if (!spec) return null;
          return (
            <article key={productKey} className={styles.card}>
              <div className={styles.cardIcon}>
                <ProductIcon productKey={productKey} />
              </div>
              <h2>{productTitle(t, productKey, spec)}</h2>
              <p>{t(`discover_products.card.${productKey}`)}</p>
              <div className={styles.actions}>
                <Button
                  type="primary"
                  href={`mailto:hello@pivony.com?subject=${spec.mailSubject}`}
                  onClick={() =>
                    trackAddonLockCtaClicked(productKey, "request_demo")
                  }
                >
                  {t("discover_products.request_demo")}
                </Button>
                <a
                  className={styles.learnMore}
                  href={spec.productUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackAddonLockCtaClicked(productKey, "read_more")
                  }
                >
                  {t("discover_products.learn_more")}
                </a>
              </div>
            </article>
          );
        })}
        <article className={styles.card}>
          <div className={styles.cardIcon}>
            <ShopOutlined />
          </div>
          <h2>{t("discover_products.shopify_title")}</h2>
          <p>{t("discover_products.shopify_body")}</p>
          <div className={styles.actions}>
            <Button
              type="primary"
              href={SONIC_PROSPECT_PRICING.shopifyAppStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("discover_products.shopify_cta")}
            </Button>
          </div>
        </article>
      </div>
    </div>
  );
};

export default DiscoverProducts;
